import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/ui/Button';
import { Spinner } from '../../../../../components/ui/Spinner';
import AppIcon from '../../../../../components/AppIcon';
import { cn } from '../../../../../utils/cn';
import { apiErrorsOf } from '../../../../../utils/requestHandling';
import {
  OVERTIME_WORKFLOW_DEFAULT_HOURS,
  OVERTIME_WORKFLOW_MAX_HOURS,
  OVERTIME_WORKFLOW_MIN_HOURS,
  deleteOvertimeWorkflow,
  getOvertimeWorkflow,
  saveOvertimeWorkflow,
  type OvertimeWorkflow,
  type OvertimeWorkflowStepInput,
} from '../../../../../api/Attendance/overtimeWorkflows';

/**
 * CO-923 — FG Clinic: "overtime nobody answers should go to HR" + "configurable like the leaves".
 * Owner decisions 2026-10-21: one company-wide workflow, Manager/HR steps, a timeout escalates to HR
 * once (default 48 h, 1-336 calendar hours), a manager's reject is final.
 *
 * Its OWN resource (api/OvertimeWorkflows) with its own Save, deliberately outside the page's
 * StickyActionBar: that bar saves CompanyRules, and folding a second endpoint into it would make one
 * click half-succeed. The route is already HR_ONLY_ROLES and the API is HrOnly.
 *
 * "No workflow" is a real option rather than a Remove button: it is today's routing, it is what an
 * unconfigured tenant actually gets, and choosing it says what happens to open requests before saving.
 * Removing is not destructive (nothing is decided or lost; open requests return to the manager), so it
 * stays inline instead of behind a confirmation dialog.
 */

type Preset = 'none' | 'managerEscalate' | 'managerThenHr' | 'hrOnly';

const PRESETS: Preset[] = ['none', 'managerEscalate', 'managerThenHr', 'hrOnly'];

function presetOf(workflow: OvertimeWorkflow | null): Preset {
  if (!workflow || workflow.steps.length === 0) return 'none';
  const shape = workflow.steps.map((s) => s.approverSource).join(',');
  if (shape === 'Manager,HR') return 'managerThenHr';
  if (shape === 'HR') return 'hrOnly';
  return 'managerEscalate';
}

function hoursOf(workflow: OvertimeWorkflow | null): string {
  return String(workflow?.steps[0]?.timeLimitHours ?? OVERTIME_WORKFLOW_DEFAULT_HOURS);
}

/** The manager step's timer is the one the reader is choosing; presets without a manager step keep the default. */
const usesTimer = (preset: Preset) => preset === 'managerEscalate' || preset === 'managerThenHr';

function stepsFor(preset: Preset, hours: number): OvertimeWorkflowStepInput[] {
  switch (preset) {
    case 'managerEscalate':
      return [{ approverSource: 'Manager', timeLimitHours: hours }];
    case 'managerThenHr':
      return [
        { approverSource: 'Manager', timeLimitHours: hours },
        { approverSource: 'HR', timeLimitHours: hours },
      ];
    case 'hrOnly':
      return [{ approverSource: 'HR', timeLimitHours: OVERTIME_WORKFLOW_DEFAULT_HOURS }];
    default:
      return [];
  }
}

export default function OvertimeApprovalWorkflowCard() {
  const { t } = useTranslation();
  const hoursId = useId();
  const hoursHelpId = useId();

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saved, setSaved] = useState<OvertimeWorkflow | null>(null);
  const [preset, setPreset] = useState<Preset>('none');
  const [hours, setHours] = useState<string>(String(OVERTIME_WORKFLOW_DEFAULT_HOURS));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const result = await getOvertimeWorkflow();
      if (result.errors?.length) {
        setLoadFailed(true);
        return;
      }
      const workflow = result.payload ?? null;
      setSaved(workflow);
      setPreset(presetOf(workflow));
      setHours(hoursOf(workflow));
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const parsedHours = Number(hours);
  const hoursValid =
    !usesTimer(preset) ||
    (Number.isInteger(parsedHours) &&
      parsedHours >= OVERTIME_WORKFLOW_MIN_HOURS &&
      parsedHours <= OVERTIME_WORKFLOW_MAX_HOURS);

  const dirty = useMemo(() => {
    const savedPreset = presetOf(saved);
    if (preset !== savedPreset) return true;
    return usesTimer(preset) && hours !== hoursOf(saved);
  }, [preset, hours, saved]);

  const presetLabel = (value: Preset) =>
    ({
      none: t('settings.overtimeWorkflow.presetNone', 'No workflow'),
      managerEscalate: t(
        'settings.overtimeWorkflow.presetManagerEscalate',
        'Manager, then HR if not actioned in time',
      ),
      managerThenHr: t('settings.overtimeWorkflow.presetManagerThenHr', 'Manager, then HR'),
      hrOnly: t('settings.overtimeWorkflow.presetHrOnly', 'HR only'),
    })[value];

  const presetBody = (value: Preset) =>
    ({
      none: t(
        'settings.overtimeWorkflow.presetNoneBody',
        "Today's routing: the employee's manager decides; for employees with no manager, the no-manager setting on this page applies. Nothing escalates.",
      ),
      managerEscalate: t(
        'settings.overtimeWorkflow.presetManagerEscalateBody',
        "The employee's manager decides. If they have not answered when the time runs out, HR can decide too.",
      ),
      managerThenHr: t(
        'settings.overtimeWorkflow.presetManagerThenHrBody',
        "The manager approves first, then HR gives the final approval. A rejection at either step ends the request; HR can still change it afterwards.",
      ),
      hrOnly: t(
        'settings.overtimeWorkflow.presetHrOnlyBody',
        'HR decides every overtime request, except for employees with approval bypass and requests the no-manager setting on this page approves automatically.',
      ),
    })[value];

  const save = async () => {
    if (!dirty || !hoursValid || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (preset === 'none') {
        const result = await deleteOvertimeWorkflow();
        if (result.errors?.length) {
          setError(result.errors[0]?.description ?? t('settings.overtimeWorkflow.saveFailed', 'The workflow could not be saved. Try again.'));
          return;
        }
        setSaved(null);
        setHours(String(OVERTIME_WORKFLOW_DEFAULT_HOURS));
        setNotice(t('settings.overtimeWorkflow.removed', 'Workflow removed. Overtime uses the default routing again.'));
        return;
      }
      const result = await saveOvertimeWorkflow(stepsFor(preset, parsedHours));
      if (result.errors?.length) {
        setError(result.errors[0]?.description ?? t('settings.overtimeWorkflow.saveFailed', 'The workflow could not be saved. Try again.'));
        return;
      }
      const workflow = result.payload ?? null;
      setSaved(workflow);
      setPreset(presetOf(workflow));
      setHours(hoursOf(workflow));
      setNotice(t('settings.overtimeWorkflow.saved', 'Workflow saved.'));
    } catch (err) {
      setError(
        apiErrorsOf(err)[0]?.description ||
          t('settings.overtimeWorkflow.saveFailed', 'The workflow could not be saved. Try again.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setPreset(presetOf(saved));
    setHours(hoursOf(saved));
    setError(null);
  };

  return (
    <section
      aria-labelledby="overtime-workflow-heading"
      className="overflow-hidden rounded-xl border border-border bg-card"
      data-testid="ot-workflow-card"
    >
      <header className="border-b border-border bg-muted/40 px-5 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id="overtime-workflow-heading" className="text-base font-semibold text-foreground">
            {t('settings.overtimeWorkflow.title', 'Overtime approval workflow')}
          </h3>
          {!loading && !loadFailed && saved && (
            <span
              className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium leading-4 text-primary"
              data-testid="ot-workflow-active-tag"
            >
              {t('settings.overtimeWorkflow.activeTag', 'Active')}
            </span>
          )}
        </div>
        <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">
          {t(
            'settings.overtimeWorkflow.intro',
            'Who decides an overtime request, and what happens when nobody answers.',
          )}
        </p>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
          <Spinner size="sm" />
          {t('settings.overtimeWorkflow.loading', 'Loading the workflow...')}
        </div>
      ) : loadFailed ? (
        <div className="flex flex-wrap items-center gap-3 px-5 py-6 text-sm" role="alert">
          <span className="text-destructive">
            {t('settings.overtimeWorkflow.loadFailed', 'The workflow could not be loaded.')}
          </span>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            {t('settings.overtimeWorkflow.retry', 'Try again')}
          </Button>
        </div>
      ) : (
        <div className="px-5 py-4">
          <div
            role="radiogroup"
            aria-label={t('settings.overtimeWorkflow.title', 'Overtime approval workflow')}
            className="space-y-2"
          >
            {PRESETS.map((value) => {
              const selected = preset === value;
              return (
                <div
                  key={value}
                  className={cn(
                    'rounded-lg border transition-colors duration-150',
                    selected ? 'border-primary bg-primary/5' : 'border-border',
                  )}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={saving}
                    onClick={() => {
                      setPreset(value);
                      setNotice(null);
                      setError(null);
                    }}
                    className={cn(
                      'w-full rounded-lg p-3 text-start',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                      !selected && 'hover:bg-muted/40',
                      saving && 'cursor-not-allowed opacity-50',
                    )}
                    data-testid={`ot-workflow-preset-${value}`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                          selected ? 'border-primary' : 'border-input',
                        )}
                        aria-hidden="true"
                      >
                        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
                      </span>
                      <span className="text-sm font-medium text-foreground">{presetLabel(value)}</span>
                      {value === 'managerEscalate' && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                          {t('settings.overtimeWorkflow.recommendedTag', 'Recommended')}
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block ps-6 text-xs text-muted-foreground">{presetBody(value)}</span>
                  </button>

                  {/* Progressive disclosure: the timer belongs to the option that uses it. */}
                  {selected && usesTimer(value) && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 pb-3 ps-9">
                      <label htmlFor={hoursId} className="text-sm text-foreground">
                        {t('settings.overtimeWorkflow.hoursLabel', 'Send to HR after')}
                      </label>
                      <span className="inline-flex items-center gap-2">
                        <input
                          id={hoursId}
                          type="number"
                          inputMode="numeric"
                          min={OVERTIME_WORKFLOW_MIN_HOURS}
                          max={OVERTIME_WORKFLOW_MAX_HOURS}
                          step={1}
                          value={hours}
                          disabled={saving}
                          aria-invalid={!hoursValid}
                          aria-describedby={hoursHelpId}
                          onChange={(e) => {
                            setHours(e.target.value);
                            setNotice(null);
                          }}
                          className={cn(
                            'h-9 w-20 rounded-md border bg-background px-2 text-sm tabular-nums text-foreground',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                            hoursValid ? 'border-input' : 'border-destructive',
                          )}
                          data-testid="ot-workflow-hours"
                        />
                        <span className="text-sm text-muted-foreground">
                          {t('settings.overtimeWorkflow.hoursUnit', 'hours')}
                        </span>
                      </span>
                      <p
                        id={hoursHelpId}
                        className={cn(
                          'w-full text-xs',
                          hoursValid ? 'text-muted-foreground' : 'text-destructive',
                        )}
                      >
                        {hoursValid
                          ? t(
                              'settings.overtimeWorkflow.hoursHelp',
                              'Calendar hours, weekends and holidays included. The request goes to HR once; the manager can still decide.',
                            )
                          : t(
                              'settings.overtimeWorkflow.hoursInvalid',
                              'Enter a whole number of hours from {{min}} to {{max}}.',
                              { min: OVERTIME_WORKFLOW_MIN_HOURS, max: OVERTIME_WORKFLOW_MAX_HOURS },
                            )}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {dirty && saved && (
            <p className="mt-3 flex max-w-[70ch] items-start gap-2 text-xs text-muted-foreground" data-testid="ot-workflow-open-requests-note">
              <AppIcon name="Info" size={14} className="mt-px shrink-0" />
              {preset === 'none'
                ? t(
                    'settings.overtimeWorkflow.removeNote',
                    "Requests waiting for a decision go back to today's routing: the employee's manager, or the no-manager setting on this page.",
                  )
                : t(
                    'settings.overtimeWorkflow.editNote',
                    'Requests waiting for a decision continue from their current step under the new workflow.',
                  )}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              onClick={() => void save()}
              disabled={!dirty || !hoursValid || saving}
              data-testid="ot-workflow-save"
            >
              {saving
                ? t('settings.overtimeWorkflow.saving', 'Saving...')
                : t('settings.overtimeWorkflow.save', 'Save workflow')}
            </Button>
            {dirty && !saving && (
              <Button variant="ghost" size="sm" onClick={reset} data-testid="ot-workflow-reset">
                {t('settings.overtimeWorkflow.discard', 'Discard changes')}
              </Button>
            )}
            <p aria-live="polite" className="text-sm">
              {error ? (
                <span className="text-destructive" data-testid="ot-workflow-error">{error}</span>
              ) : notice ? (
                <span className="text-muted-foreground" data-testid="ot-workflow-notice">{notice}</span>
              ) : null}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
